import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API from "../../API/Main/API";
import {
  BillingSessionsProperties,
  GetBillingProperties,
  GetTotalBillingProperties,
  MBHLogsProperties,
} from "../../API/ClientAPIHelpers/billingProperties";
import { ErrorProperties } from "../../API/identityAPIProperties";

interface BillingFilterProperties {
  clientId: string;
  clientSearch: string;
  clientIds: Array<string>;
  userId: string;
  userSearch: string;
  onlyNew: boolean;
  startDate: string;
  endDate: string;
  reportIds: Array<string>;
  timeError: boolean;
}

interface BillingInitialStateProperties {
  loading: boolean;
  mbhSessions: BillingSessionsProperties;
  backdatedSessions: BillingSessionsProperties;
  ncSessions: BillingSessionsProperties;
  lpSessions: BillingSessionsProperties;
  nbSessions: BillingSessionsProperties;
  billingFilters: BillingFilterProperties;
  mbhLogsStatus: MBHLogsProperties | null;
  error: ErrorProperties;
}

const initialState: BillingInitialStateProperties = {
  loading: false,
  mbhLogsStatus: null,
  mbhSessions: {
    query: null,
  },
  backdatedSessions: {
    query: null,
  },
  ncSessions: {
    query: null,
  },
  lpSessions: {
    query: null,
  },
  nbSessions: {
    query: null,
  },
  billingFilters: {
    clientId: "",
    clientIds: [],
    endDate: "",
    onlyNew: true,
    startDate: "",
    userId: "",
    clientSearch: "",
    userSearch: "",
    timeError: false,
    reportIds: [],
  },
  error: {
    status: 0,
    title: "",
  },
};

export const getMBHLogs = createAsyncThunk("billing/mbh/logs", async (_, thunkAPI) => {
  const response = await API.ClientAPI.Billing.MBHLogs.getMBHLogs();
  if (!!response.error) {
    return thunkAPI.rejectWithValue(response.error);
  }
  return response as MBHLogsProperties;
});

export const getNonBillingSessions = createAsyncThunk(
  "billing/sessions/non-billable",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.getNonBillableSessions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as BillingSessionsProperties;
  }
);

export const getBillingSessions = createAsyncThunk(
  "billing/sessions",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.getSessions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as BillingSessionsProperties;
  }
);

export const getNonCredentialedSessions = createAsyncThunk(
  "billing/sessions/non-credentialed",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.getNonCredentialedSessions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as BillingSessionsProperties;
  }
);

export const downloadMBHExcelReport = createAsyncThunk(
  "billing/sessions/download/mbh",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.downloadMBHExcelReport(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as {
      file: Blob;
      fileName: string;
    };
  }
);

export const downloadNonBillableExcelReport = createAsyncThunk(
  "billing/sessions/download/non-billable",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.downloadNonBillableExcelReport(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as {
      file: Blob;
      fileName: string;
    };
  }
);

export const downloadNCExcelReport = createAsyncThunk(
  "billing/sessions/download/nc",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.downloadNCExcelReport(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as {
      file: Blob;
      fileName: string;
    };
  }
);

export const downloadTotalReport = createAsyncThunk(
  "billing/sessions/download/total",
  async (data: GetTotalBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.downloadTotalSesions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response;
  }
);

export const getLimitedPermitSessions = createAsyncThunk(
  "billing/sessions/limited-permit",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.getLimitedPermitSessions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as BillingSessionsProperties;
  }
);

export const downloadLimitedPermitSessions = createAsyncThunk(
  "billing/sessions/limited-permit/download",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.downloadLimitedPermitSessions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as {
      file: Blob;
      fileName: string;
    };
  }
);

export const getBackdatedSessions = createAsyncThunk(
  "billing/backdated/sessions",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.getBackdatedSessions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as BillingSessionsProperties;
  }
);

export const downloadBackdatedSessions = createAsyncThunk(
  "billing/backdated/sessions/download",
  async (data: GetBillingProperties, thunkAPI) => {
    const response = await API.ClientAPI.Billing.downloadBackdatedSessions(data);
    if (!!response.error) {
      return thunkAPI.rejectWithValue(response.error);
    }
    return response as {
      file: Blob;
      fileName: string;
    };
  }
);

const BillingSlice = createSlice({
  name: "billing",
  initialState: initialState,
  reducers: {
    clearFilterData(state) {
      state.billingFilters = initialState.billingFilters;
    },
    setClientId(state, action: { payload: string; type: string }) {
      state.billingFilters.clientId = action.payload;
    },
    setUserId(state, action: { payload: string; type: string }) {
      state.billingFilters.userId = action.payload;
    },
    setSearchStringClient(state, action: { payload: string; type: string }) {
      state.billingFilters.clientSearch = action.payload;
    },
    setSearchStringUser(state, action: { payload: string; type: string }) {
      state.billingFilters.userSearch = action.payload;
    },
    setStartDate(state, action: { payload: string; type: string }) {
      state.billingFilters.startDate = action.payload;
    },
    setEndDate(state, action: { payload: string; type: string }) {
      state.billingFilters.endDate = action.payload;
    },
    setOnlyNew(state, action: { payload: boolean; type: string }) {
      state.billingFilters.onlyNew = action.payload;
    },
    setReportIds(state, action) {
      state.billingFilters.reportIds = action.payload;
    },
    setClientIds(state, action) {
      state.billingFilters.clientIds = action.payload;
    },
    setTimeError(state, action) {
      state.billingFilters.timeError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBillingSessions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBillingSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.mbhSessions = action.payload;
      state.error = initialState.error;
    });
    builder.addCase(getBillingSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(getNonBillingSessions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNonBillingSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.nbSessions = action.payload;
      state.error = initialState.error;
    });
    builder.addCase(getNonBillingSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(getNonCredentialedSessions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNonCredentialedSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.ncSessions = action.payload;
      state.error = initialState.error;
    });
    builder.addCase(getNonCredentialedSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(downloadMBHExcelReport.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(downloadMBHExcelReport.fulfilled, (state) => {
      state.loading = false;
      state.error = initialState.error;
    });
    builder.addCase(downloadMBHExcelReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(downloadNonBillableExcelReport.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(downloadNonBillableExcelReport.fulfilled, (state) => {
      state.loading = false;
      state.error = initialState.error;
    });
    builder.addCase(downloadNonBillableExcelReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(downloadTotalReport.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(downloadTotalReport.fulfilled, (state) => {
      state.loading = false;
      state.error = initialState.error;
    });
    builder.addCase(downloadTotalReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(downloadNCExcelReport.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(downloadNCExcelReport.fulfilled, (state) => {
      state.loading = false;
      state.error = initialState.error;
    });
    builder.addCase(downloadNCExcelReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(getLimitedPermitSessions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getLimitedPermitSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.lpSessions = action.payload;
      state.error = initialState.error;
    });
    builder.addCase(getLimitedPermitSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(downloadLimitedPermitSessions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(downloadLimitedPermitSessions.fulfilled, (state) => {
      state.loading = false;
      state.error = initialState.error;
    });
    builder.addCase(downloadLimitedPermitSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(getBackdatedSessions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBackdatedSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.backdatedSessions = action.payload;
      state.error = initialState.error;
    });
    builder.addCase(getBackdatedSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    builder.addCase(getMBHLogs.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getMBHLogs.fulfilled, (state, action) => {
      state.loading = false;
      state.mbhLogsStatus = action.payload;
      state.error = initialState.error;
    });
    builder.addCase(getMBHLogs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
  },
});

export const {
  setClientId,
  setEndDate,
  setOnlyNew,
  setStartDate,
  setUserId,
  setSearchStringClient,
  setSearchStringUser,
  clearFilterData,
  setReportIds,
  setClientIds,
  setTimeError,
} = BillingSlice.actions;
export default BillingSlice.reducer;
